import React from 'react';
import { CheckCircle2, Clock, ChefHat, ShoppingBag, Bike, PackageCheck, XCircle, Search, Car } from 'lucide-react';
import { CustomerTrackingEvent, OrderStatus } from '../../lib/types';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../lib/i18n';
interface StatusTimelineProps {
  currentStatus: OrderStatus;
  events: CustomerTrackingEvent[];
  restaurantId?: string;
  deliveryType?: string;
}
const BASE_STATUS_FLOW = ['pending', 'confirmed', 'preparing', 'ready'];
const DELIVERY_STATUS_FLOW = [...BASE_STATUS_FLOW, 'searching_driver', 'out_for_delivery', 'delivered'];
const PICKUP_STATUS_FLOW = [...BASE_STATUS_FLOW, 'delivered'];
const STATUS_ICONS = {
  pending: {
    icon: ChefHat,
    color: 'text-gray-500',
    bg: 'bg-gray-50'
  },
  confirmed: {
    icon: ShoppingBag,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50'
  },
  preparing: {
    icon: Bike,
    color: 'text-amber-500',
    bg: 'bg-amber-50'
  },
  ready: {
    icon: Clock,
    color: 'text-blue-500',
    bg: 'bg-blue-50'
  },
  searching_driver: {
    icon: Search,
    color: 'text-orange-500',
    bg: 'bg-orange-50'
  },
  out_for_delivery: {
    icon: Car,
    color: 'text-cyan-500',
    bg: 'bg-cyan-50'
  },
  delivered: {
    icon: PackageCheck,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500'
  },
  cancelled: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-500'
  }
};
export function StatusTimeline({
  currentStatus,
  events,
  deliveryType
}: StatusTimelineProps) {
  const {
    t
  } = useLanguage();
  const STATUS_FLOW = deliveryType === 'delivery' ? DELIVERY_STATUS_FLOW : PICKUP_STATUS_FLOW;
  // Find the index of the current status in the flow
  // If current status is not in the flow (e.g., cancelled), it will be -1
  const currentIndex = STATUS_FLOW.indexOf(currentStatus);
  // If cancelled, show a specific cancelled state
  if (currentStatus === 'cancelled') {
    return <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-center" data-id="element-1365">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1366">
          <XCircle className="w-8 h-8 text-red-500" data-id="element-1367" />
        </div>
        <h3 className="text-xl font-bold text-red-900 mb-2" data-id="element-1368">
          {t('status_cancelled')}
        </h3>
        <p className="text-red-700" data-id="element-1369">
          Lo sentimos, este pedido ha sido cancelado. Si tienes dudas, por favor
          contacta al restaurante.
        </p>
      </div>;
  }
  return <div className="relative pl-4 sm:pl-8 py-4" data-id="element-1370">
      {/* Vertical Line */}
      <div className="absolute left-7 sm:left-11 top-8 bottom-8 w-0.5 bg-gray-200" data-id="element-1371" />

      <div className="space-y-8 relative" data-id="element-1372">
        {STATUS_FLOW.map((status, index) => {
        // A status is completed if we are past it in the flow, OR if we are at the end of the flow and this is the last step
        const isCompleted = currentIndex !== -1 && index <= currentIndex;
        const isCurrent = index === currentIndex;
        const isLast = index === STATUS_FLOW.length - 1;
        const event = events.find(e => e.status === status);
        const Icon = STATUS_ICONS[status as keyof typeof STATUS_ICONS].icon;
        let statusLabel = t(`status_${status}`);
        if (status === 'searching_driver') statusLabel = 'Buscando Conductor';
        if (status === 'out_for_delivery') statusLabel = 'En Camino';
        return <div key={status} className="relative flex gap-4" data-id="element-1373">
              {/* Timeline Line */}
              {!isLast && <div className={cn('absolute left-4 top-10 bottom-[-24px] w-0.5 -translate-x-1/2', isCompleted ? 'bg-brand-primary' : 'bg-gray-200')} data-id="element-1374" />}

              {/* Icon */}
              <div className={cn('relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 bg-white shrink-0', isCompleted || isCurrent ? 'border-brand-primary text-brand-primary' : 'border-gray-300 text-gray-400', isCurrent && 'ring-4 ring-brand-primary/20')} data-id="element-1375">
                <Icon className="w-5 h-5" data-id="element-1376" />
              </div>

              {/* Content */}
              <div className="flex-1 pb-8" data-id="element-1377">
                <h3 className={cn('text-base font-bold mb-1', isCompleted || isCurrent ? 'text-gray-900' : 'text-gray-500')} data-id="element-1378">
                  {statusLabel}
                </h3>

                {event && <div className="bg-gray-50 rounded-xl p-3 mt-2 border border-gray-100" data-id="element-1379">
                    <p className="text-sm text-gray-600" data-id="element-1380">{event.message}</p>
                    <p className="text-xs text-gray-400 mt-2 font-medium" data-id="element-1381">
                      {new Date(event.created_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                    </p>
                  </div>}
              </div>
            </div>;
      })}
      </div>
    </div>;
}